import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, ChevronDown, X } from "lucide-react";
import Logo from "@/components/Logo";
import { PillButton } from "./PillButton";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

type NavGroup = { label: string; items: { label: string; to: string; desc?: string }[] };

const groups: NavGroup[] = [
  {
    label: "Product",
    items: [
      { label: "Anonymous Feedback", to: "/how-anonymity-works", desc: "True zero-knowledge architecture" },
      { label: "AI Insights", to: "/#features", desc: "Sentiment, themes & risk alerts" },
      { label: "HR Action Plans", to: "/#features", desc: "Close the loop on every signal" },
      { label: "Pulse Campaigns", to: "/#features", desc: "Continuous, lightweight check-ins" },
    ],
  },
  {
    label: "Use Cases",
    items: [
      { label: "HR Teams", to: "/solutions/hr-teams" },
      { label: "Remote Teams", to: "/solutions/remote-teams" },
      { label: "Enterprise", to: "/solutions/enterprise" },
      { label: "Startups", to: "/solutions/startups" },
      { label: "Culture Building", to: "/solutions/culture-building" },
      { label: "Employee Retention", to: "/solutions/employee-retention" },
    ],
  },
  {
    label: "Resources",
    items: [
      { label: "Help Center", to: "/help" },
      { label: "Track Feedback", to: "/track" },
      { label: "About", to: "/about" },
      { label: "Contact", to: "/contact" },
    ],
  },
];

export const MarketingNav = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-border/60">
      <div className="container mx-auto px-4 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <Logo size="md" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {groups.map((g) => (
            <div
              key={g.label}
              className="relative"
              onMouseEnter={() => setOpen(g.label)}
              onMouseLeave={() => setOpen(null)}
            >
              <button className="h-10 px-4 inline-flex items-center gap-1 text-sm font-medium text-foreground/80 hover:text-foreground rounded-full hover:bg-foreground/[0.04] transition">
                {g.label}
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </button>
              {open === g.label && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 min-w-[280px]">
                  <div className="bg-white rounded-2xl border border-border shadow-xl p-2">
                    {g.items.map((it) => (
                      <Link
                        key={it.to + it.label}
                        to={it.to}
                        className="block px-4 py-3 rounded-xl hover:bg-muted transition"
                      >
                        <div className="text-sm font-medium text-foreground">{it.label}</div>
                        {it.desc && (
                          <div className="text-xs text-muted-foreground mt-0.5">{it.desc}</div>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          <Link
            to="/pricing"
            className="h-10 px-4 inline-flex items-center text-sm font-medium text-foreground/80 hover:text-foreground rounded-full hover:bg-foreground/[0.04] transition"
          >
            Pricing
          </Link>
        </nav>

        {/* Right CTAs */}
        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <PillButton variant="primary" size="md" onClick={() => navigate("/dashboard")}>
              Dashboard
            </PillButton>
          ) : (
            <>
              <Link to="/auth" className="text-sm font-medium px-3 py-2 text-foreground/80 hover:text-foreground">
                Login
              </Link>
              <PillButton variant="outline" size="md" onClick={() => navigate("/auth?mode=signup")}>
                Get Started
              </PillButton>
              <PillButton variant="primary" size="md" onClick={() => navigate("/contact")}>
                Book Demo
              </PillButton>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="lg:hidden flex items-center gap-2">
          {!user && (
            <PillButton variant="primary" size="sm" onClick={() => navigate("/auth?mode=signup")}>
              Start Free
            </PillButton>
          )}
          <button
            className="h-10 w-10 inline-flex items-center justify-center rounded-full hover:bg-foreground/[0.05]"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-border bg-white">
          <div className="container mx-auto px-4 py-4 space-y-4 max-h-[calc(100vh-72px)] overflow-y-auto">
            {groups.map((g) => (
              <div key={g.label}>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2 mb-1">
                  {g.label}
                </div>
                <div className="flex flex-col">
                  {g.items.map((it) => (
                    <Link
                      key={it.to + it.label}
                      to={it.to}
                      onClick={() => setMobileOpen(false)}
                      className="px-2 py-2.5 text-[15px] font-medium text-foreground rounded-lg hover:bg-muted"
                    >
                      {it.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
            <Link
              to="/pricing"
              onClick={() => setMobileOpen(false)}
              className="block px-2 py-2.5 text-[15px] font-medium text-foreground"
            >
              Pricing
            </Link>
            <div className="pt-2 border-t border-border flex flex-col gap-2">
              {user ? (
                <PillButton variant="primary" size="lg" onClick={() => { navigate("/dashboard"); setMobileOpen(false); }}>
                  Open Dashboard
                </PillButton>
              ) : (
                <>
                  <PillButton variant="outline" size="lg" onClick={() => { navigate("/auth"); setMobileOpen(false); }}>
                    Login
                  </PillButton>
                  <PillButton variant="primary" size="lg" onClick={() => { navigate("/auth?mode=signup"); setMobileOpen(false); }}>
                    Get Started
                  </PillButton>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
